import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Button, Form, Image } from 'react-bootstrap';
import { HiEye, HiEyeOff } from 'react-icons/hi';

import { resetPassword, signOut } from '../../store/actions/users';
import { errorHelper } from '../../utils/tools';

import '../../styles/style.css';
import '../../styles/tenantSetup.css';

import logo from '../../assets/logo.svg';

const ResetPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const token = searchParams.get('token');

    // Odkaz na nastavenie hesla môže otvoriť niekto, kto je v tomto prehliadači ešte
    // prihlásený ako iný účet (napr. owner testujúci novovytvoreného používateľa) -
    // reláciu vždy ukončíme (aj keby o nej Redux ešte nevedel, napr. v novej záložke),
    // aby po nastavení hesla naozaj videl prihlasovaciu obrazovku pre správny účet.
    useEffect(() => {
        dispatch(signOut());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formik = useFormik({
        initialValues: { password: '', passwordConfirm: '' },
        validationSchema: Yup.object({
            password: Yup.string()
                .min(8, 'Heslo musí mať aspoň 8 znakov.')
                .max(20, 'Heslo môže mať maximálne 20 znakov.')
                .matches(/^(?=.*[A-Z])/, 'Heslo musí obsahovať aspoň jedno veľké písmeno.')
                .required('Zadajte nové heslo.'),
            passwordConfirm: Yup.string()
                .oneOf([Yup.ref('password')], 'Heslá sa nezhodujú.')
                .required('Potvrďte nové heslo.')
        }),
        onSubmit: async (values) => {
            if (!token) return;
            setLoading(true);
            try {
                await dispatch(resetPassword({ token, password: values.password })).unwrap();
                navigate('/account/login');
            } catch {
                // error displayed via toast
            } finally {
                setLoading(false);
            }
        }
    });

    if (!token) {
        return (
            <div className="onboarding-overlay">
                <div className="onboarding-left">
                    <div className="onboarding-left-content">
                        <h2>Evidencia odpadov jednoducho a efektívne.</h2>
                        <p>Prihlásením získate prístup k evidenčným listom, skladom a ohlasovaniu odpadov vašej organizácie.</p>
                    </div>
                </div>
                <div className="onboarding-right auth-right">
                    <Image src={logo} style={{ height: '32px', width: 'auto', position: 'absolute', top: '32px', right: '48px' }} />
                    <div className="auth-form-inner">
                        <div className="mb-4">
                            <h1>Neplatný odkaz</h1>
                            <p style={{ color: '#718094' }}>Odkaz na obnovu hesla je neplatný alebo vypršal.</p>
                        </div>
                        <p className="auth-register-hint">
                            <Link to="/account/forgot-password" className="auth-register-link">
                                Požiadať o nový odkaz
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onboarding-overlay">
            <div className="onboarding-left">
                <div className="onboarding-left-content">
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        <span style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '20px', padding: '4px 14px', fontSize: '0.78rem', color: '#fff', fontWeight: 500, letterSpacing: '0.02em' }}>Evidencia odpadov</span>
                        <span style={{ background: 'rgba(99,179,237,0.15)', border: '1px solid rgba(99,179,237,0.4)', borderRadius: '20px', padding: '4px 14px', fontSize: '0.78rem', color: '#90cdf4', fontWeight: 500, letterSpacing: '0.02em' }}>PPWR</span>
                    </div>
                    <h2>Plný súlad s legislatívou.<br/>Jednoducho a efektívne.</h2>
                    <p>Komplexná platforma pre evidenciu odpadov aj reporting obalov podľa nariadenia PPWR – všetko na jednom mieste.</p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '20px 0 0 0' }}>
                        {[
                            'Evidenčné listy a ohlasovanie odpadov',
                            'Správa skladov a tok odpadov',
                            'PPWR – evidencia a reporting obalov',
                            'Exporty a štatistiky pre úrady',
                        ].map((item, i) => (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', fontSize: '0.9rem', color: 'rgba(255,255,255,0.88)' }}>
                                <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.65rem' }}>✓</span>
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="onboarding-right auth-right">
                <Image src={logo} style={{ height: '32px', width: 'auto', position: 'absolute', top: '32px', right: '48px' }} />

                <div className="auth-form-inner">
                    <div className="mb-4">
                        <h1>Obnoviť heslo</h1>
                        <p style={{ color: '#718094' }}>Zadajte nové heslo pre váš účet.</p>
                    </div>

                    <Form onSubmit={formik.handleSubmit}>
                        <Form.Group className="mb-3" controlId="resetPassword">
                            <Form.Label>Nové heslo</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showPassword ? 'text' : 'password'}
                                    className="radius"
                                    placeholder="Zadajte nové heslo:"
                                    style={{ paddingRight: '2.5rem', backgroundImage: 'none' }}
                                    {...formik.getFieldProps('password')}
                                    isInvalid={errorHelper(formik, 'password').error}
                                />
                                <span
                                    onClick={() => setShowPassword(p => !p)}
                                    style={{ position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6c757d', zIndex: 5 }}
                                >
                                    {showPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                                </span>
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: errorHelper(formik, 'password').error ? 'block' : 'none' }}>
                                {errorHelper(formik, 'password').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="resetPasswordConfirm">
                            <Form.Label>Potvrdiť heslo</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className="radius"
                                    placeholder="Zopakujte nové heslo:"
                                    style={{ paddingRight: '2.5rem', backgroundImage: 'none' }}
                                    {...formik.getFieldProps('passwordConfirm')}
                                    isInvalid={errorHelper(formik, 'passwordConfirm').error}
                                />
                                <span
                                    onClick={() => setShowConfirmPassword(p => !p)}
                                    style={{ position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6c757d', zIndex: 5 }}
                                >
                                    {showConfirmPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                                </span>
                            </div>
                            <Form.Control.Feedback type="invalid" style={{ display: errorHelper(formik, 'passwordConfirm').error ? 'block' : 'none' }}>
                                {errorHelper(formik, 'passwordConfirm').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            className="full-width button-custom-large radius"
                            disabled={loading}
                        >
                            {loading ? 'Ukladám...' : 'Nastaviť nové heslo'}
                        </Button>
                    </Form>

                    <p className="auth-register-hint">
                        <Link to="/account/login" className="auth-register-link">
                            Späť na prihlásenie
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
